import React, { Dispatch, SetStateAction } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { formatToShortDate, formatUniversalNumber } from "../../../lib/utils";
import type { JournalEntryStrings, VoucherData } from "../types";
import { motion } from "framer-motion";
import DetailsCard from "./DetailsCard";

interface JournalModalPropType {
  open: boolean;
  setIsJournalModalOpen: Dispatch<SetStateAction<boolean>>;
  voucherData: VoucherData | null;
  isLoading: boolean;
  JournalModalStrings?: JournalEntryStrings;
}

const JournalDataEditModal = ({
  open,
  setIsJournalModalOpen,
  voucherData,
  isLoading,
  JournalModalStrings,
}: JournalModalPropType) => {
  return (
    <Dialog open={open} onOpenChange={setIsJournalModalOpen}>
      <DialogContent className="p-0 max-w-3xl overflow-hidden">
        <DialogHeader className="p-6">
          <DialogTitle className="!text-xl">
            {JournalModalStrings?.modalLabel}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-4 max-h-[80vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[200px]">
              <span className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></span>
            </div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Source Information */}
              <div className="BookEZ_card_bg BookEZ_card_radius border BookEZ_card_border_color shadow-sm overflow-hidden">
                <div className="BookEZ_transaction_header_bg px-4 py-3 border-b BookEZ_card_border_color">
                  <h3 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
                    {JournalModalStrings?.sourceDetails?.label}
                  </h3>
                </div>
                <div className="p-5">
                  <div className="relative">
                    <div
                      className="absolute left-2.5 md:left-2.5 top-[12px]"
                      style={{
                        height: "calc(100% - 40px)",
                        width: "0px",
                        borderLeft: "2px dashed rgb(59 130 246)",
                      }}
                    ></div>

                    <div className="space-y-6">
                      {[
                        {
                          label:
                            JournalModalStrings?.sourceDetails?.sourceLabel,
                          value: voucherData?.entryType,
                          icon: "file-text",
                        },
                        {
                          label:
                            JournalModalStrings?.sourceDetails?.recipientLabel,
                          value: voucherData?.customerName,
                          icon: "user",
                        },
                        {
                          label: JournalModalStrings?.sourceDetails?.dateLabel,
                          value: voucherData?.remarks || "-",
                          icon: "message-square",
                        },
                      ].map((item) => (
                        <div
                          className="flex items-start gap-4"
                          id="sourceDetails"
                          key={item.label}
                        >
                          {/* Dot */}
                          <div className="flex-shrink-0 relative">
                            <div className="h-5 w-5 rounded-full border-2 border-blue-500 bg-white z-10 flex items-center justify-center">
                              {/* <div className="h-2 w-2 rounded-full bg-blue-500"></div> */}
                            </div>
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-1">
                              <div className="w-full md:w-1/3 BookEZ_font_body_medium BookEZ_font_body_size BookEZ_font_color_gray_2">
                                {item.label}
                              </div>
                              <div className="w-full md:w-2/3 BookEZ_font_body_medium BookEZ_font_body_size BookEZ_font_color_gray_1">
                                {item.value || "-"}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {voucherData?.invoiceDetails && (
                <div className="mt-4" id="invoiceDetails">
                  <div className="BookEZ_transaction_header_bg border BookEZ_card_border_color shadow-sm BookEZ_card_radius p-5 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex items-center gap-3">
                      <span className="inline-block BookEZ_bg_blue_light BookEZ_font_color_blue rounded-full px-3 py-1 text-xs BookEZ_font_bold">
                        {JournalModalStrings?.invoice?.invoiceTagLabel}
                      </span>
                      <span className="BookEZ_font_body_size_large BookEZ_font_bold BookEZ_font_color_gray_2 tracking-wide">
                        {voucherData.invoiceDetails.refInvoiceId || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="inline-block BookEZ_bg_green_light BookEZ_font_color_green rounded-full px-3 py-1 text-xs BookEZ_font_bold">
                        {JournalModalStrings?.invoice?.invoiceAmountTagLabel}
                      </span>
                      <span
                        className={`BookEZ_font_body_size_large font-bold tracking-wide`}
                      >
                        {voucherData.invoiceDetails.invoiceAmount <= 0 ? (
                          <span className="BookEZ_font_color_red">
                            {formatUniversalNumber(voucherData.totalAmt)}
                          </span>
                        ) : (
                          <span className="BookEZ_font_color_green">
                            {formatUniversalNumber(voucherData.totalAmt)}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {voucherData?.paymentDetails && (
                <DetailsCard
                  title={
                    JournalModalStrings?.paymentDetails?.headerLabel || "-"
                  }
                  details={[
                    {
                      label:
                        JournalModalStrings?.paymentDetails?.paymentTagLabel ||
                        "-",
                      value: voucherData.paymentDetails.externalId.replace(
                        /(\s.*)/,
                        ""
                      ),
                      type: "id",
                    },
                    {
                      label:
                        JournalModalStrings?.paymentDetails?.totalLabel || "-",
                      value: formatUniversalNumber(voucherData.totalAmt),
                      type: "amount",
                    },
                    {
                      label:
                        JournalModalStrings?.paymentDetails?.paymentDateLabel ||
                        "-",
                      value: voucherData.paymentDetails.paymentDate,
                      type: "date",
                    },
                  ]}
                  stringOverrides={JournalModalStrings?.invoiceList}
                  invoices={voucherData.paymentDetails.invoices}
                />
              )}

              {voucherData?.creditMemoDetails && (
                <DetailsCard
                  title={
                    JournalModalStrings?.creditMemoList
                      ?.creditMemoTableHeaderLabel || "bdjhdvsajdas"
                  }
                  details={[
                    {
                      label:
                        JournalModalStrings?.creditMemoList
                          ?.creditMemoIdLabel || "-",
                      value: voucherData.creditMemoDetails.externalId,
                      type: "id",
                    },
                    {
                      label:
                        JournalModalStrings?.creditMemoList
                          ?.creditMemoAmountLabel || "-",
                      value: formatUniversalNumber(
                        voucherData.creditMemoDetails.totalAmt
                      ),
                      type: "amount",
                    },
                    {
                      label:
                        JournalModalStrings?.creditMemoList
                          ?.creditMemoDateLabel || "-",
                      value: voucherData.creditMemoDetails.paymentDate,
                      type: "date",
                    },
                  ]}
                  stringOverrides={JournalModalStrings?.creditMemoList}
                  invoices={voucherData.creditMemoDetails.paymentDetails.map(
                    (item) => ({
                      refInvoiceId: item.externalId,
                      invoiceAmount: item.creditMemoAmount,
                      redemptionTypeAccountName: formatToShortDate(
                        item.memoDate
                      ),
                    })
                  )}
                />
              )}

              {voucherData?.voucherWriteOffDetails && (
                <DetailsCard
                  title={
                    JournalModalStrings?.voucherWriteOffDetails?.headerLabel ||
                    "-"
                  }
                  details={[
                    {
                      label:
                        JournalModalStrings?.voucherWriteOffDetails
                          ?.voucherIdLabel || "-",
                      value: voucherData.voucherWriteOffDetails.externalId,
                      type: "id",
                      badge: "ID",
                    },
                    {
                      label:
                        JournalModalStrings?.voucherWriteOffDetails
                          ?.totalAmountLabel || "-",
                      value: formatUniversalNumber(voucherData.totalAmt),
                      type: "amount",
                    },
                    {
                      label:
                        JournalModalStrings?.voucherWriteOffDetails
                          ?.writeOffDateLabel || "-",
                      value: voucherData.voucherWriteOffDetails.writeOffDate,
                      type: "date",
                    },
                  ]}
                  stringOverrides={JournalModalStrings?.invoiceList}
                  invoices={voucherData.voucherWriteOffDetails.invoices}
                />
              )}

              {voucherData?.refundDetails && (
                <DetailsCard
                  title={JournalModalStrings?.refundDetails?.headerLabel || "-"}
                  details={[
                    {
                      label:
                        JournalModalStrings?.refundDetails?.refundIdLabel ||
                        "-",
                      value: voucherData.refundDetails.externalId,
                      type: "id",
                      badge: "ID",
                    },
                    {
                      label:
                        JournalModalStrings?.refundDetails?.totalAmountLabel ||
                        "-",
                      value: formatUniversalNumber(
                        voucherData.refundDetails.totalAmt
                      ),
                      type: "amount",
                    },
                    {
                      label:
                        JournalModalStrings?.refundDetails?.refundDateLabel ||
                        "-",
                      value: voucherData.refundDetails.refundDate,
                      type: "date",
                    },
                  ]}
                  stringOverrides={JournalModalStrings?.invoiceList}
                  invoices={voucherData.refundDetails.invoices}
                />
              )}
              {/* Transaction Details */}
              <div
                className="BookEZ_card_bg BookEZ_card_radius border BookEZ_card_border_color shadow-sm overflow-hidden"
                id="transactionTimeLine"
              >
                <div className="BookEZ_transaction_header_bg px-4 py-3 border-b BookEZ_card_border_color">
                  <h3 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
                    {JournalModalStrings?.transactionTimeLine?.transactionLabel}
                  </h3>
                </div>
                <div className="p-5">
                  <div className="relative">
                    <div
                      className="absolute left-2.5 md:left-2.5 top-[12px]"
                      style={{
                        height: "calc(100% - 40px)",
                        width: "0px",
                        borderLeft: "2px dashed rgb(59 130 246)",
                      }}
                    ></div>

                    <div className="space-y-6">
                      {[
                        {
                          label:
                            JournalModalStrings?.transactionTimeLine
                              ?.effectiveDateLabel,
                          value: formatToShortDate(voucherData?.voucherDate),
                        },
                        {
                          label:
                            JournalModalStrings?.transactionTimeLine
                              ?.creationDateLabel,
                          value: formatToShortDate(
                            voucherData?.createdDateTime
                          ),
                        },
                        {
                          label:
                            JournalModalStrings?.transactionTimeLine
                              ?.reversalLabel,
                          value: voucherData?.reversalRemarks || "-",
                        },
                      ].map((item) => (
                        <div
                          className="flex items-start gap-4"
                          key={item.label}
                        >
                          {/* Dot */}
                          <div className="flex-shrink-0 relative">
                            <div className="h-5 w-5 rounded-full border-2 border-blue-500 bg-white z-10 flex items-center justify-center">
                              {/* <div className="h-2 w-2 rounded-full bg-blue-500"></div> */}
                            </div>
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-1">
                              <div className="w-full md:w-1/3 BookEZ_font_body_medium BookEZ_font_body_size BookEZ_font_color_gray_2">
                                {item.label}
                              </div>
                              <div className="w-full md:w-2/3 BookEZ_font_body_medium BookEZ_font_body_size BookEZ_font_color_gray_1">
                                {item.value}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="BookEZ_card_bg BookEZ_card_radius border BookEZ_card_border_color shadow-sm overflow-hidden">
                <div className="BookEZ_transaction_header_bg px-4 py-3 border-b BookEZ_card_border_color">
                  <h3 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
                    {JournalModalStrings?.journalDetails?.headerLabel}
                  </h3>
                </div>
                <div>
                  {/* Header Row */}
                  <div className="grid grid-cols-3 BookEZ_transaction_header_bg p-4 border-b BookEZ_card_border_color">
                    <div className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
                      {JournalModalStrings?.journalDetails?.accountLabel}
                    </div>
                    <div className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 text-right">
                      {JournalModalStrings?.journalDetails?.debitLabel}
                    </div>
                    <div className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 text-right">
                      {JournalModalStrings?.journalDetails?.creditLabel}
                    </div>
                  </div>

                  {/* Data Rows */}
                  {voucherData?.journalDetails?.map((item: any) => (
                    <div
                      className="grid grid-cols-3 p-4 border-b last:border-b-0 BookEZ_card_border_color hover:bg-[#fbfafa12] transition-colors"
                      key={item?.entryId}
                    >
                      <div className="BookEZ_font_body_size flex items-center BookEZ_font_color_gray_2 BookEZ_font_body_medium truncate max-w-[200px]">
                        {item.creditAmt > 0
                          ? item?.creditAccount
                          : item?.debitAccount}
                      </div>
                      <div className="text-right">
                        {item?.debitAmt > 0 && (
                          <span className="inline-block BookEZ_bg_yellow_light BookEZ_font_color_yellow BookEZ_font_body_size BookEZ_font_body_medium px-3 py-1 rounded-full">
                            {formatUniversalNumber(item?.debitAmt)}
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        {item?.creditAmt > 0 && (
                          <span className="inline-block BookEZ_bg_green_light BookEZ_font_color_green BookEZ_font_body_size BookEZ_font_body_medium px-3 py-1 rounded-full">
                            {formatUniversalNumber(item?.creditAmt)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Total Row */}
                  <div className="grid grid-cols-3 p-4 border-t-2 border-gray-200 BookEZ_transaction_header_bg">
                    <div className="BookEZ_font_body_size BookEZ_font_bold BookEZ_font_color_gray_2 flex items-center">
                      {JournalModalStrings?.journalDetails?.totalLabel}
                    </div>
                    <div className="text-right">
                      <span className="inline-block bg-amber-100 text-amber-800 BookEZ_font_body_size BookEZ_font_bold px-3 py-1 rounded-full">
                        {formatUniversalNumber(
                          voucherData?.journalDetails?.reduce(
                            (sum: number, item: any) =>
                              sum + (item.debitAmt || 0),
                            0
                          )
                        )}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="inline-block BookEZ_bg_green_light BookEZ_font_color_green BookEZ_font_body_size BookEZ_font_bold px-3 py-1 rounded-full">
                        {formatUniversalNumber(
                          voucherData?.journalDetails?.reduce(
                            (sum: number, item: any) =>
                              sum + (item.creditAmt || 0),
                            0
                          )
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JournalDataEditModal;
